import { StorageAPI, StorageOptions, ApiResponse } from '../types';
import { ApiClient } from '../utils/api';
import { LRUCache, CacheOptions } from '../utils/cache';

/**
 * Service for key-value storage with versioning and subscriptions
 */
export class StorageService implements StorageAPI {
  private apiClient: ApiClient;
  private gameId: string;
  private namespace: string;
  private cache: LRUCache<{ value: any; version: number }>;
  private quotaLimit: number | null = null;
  private quotaUsage: number = 0;

  constructor(apiClient: ApiClient, options: StorageOptions) {
    this.apiClient = apiClient;
    this.gameId = options.gameId;
    this.namespace = options.namespace || 'default';

    // Initialize cache with options
    const cacheOptions: CacheOptions = {
      maxSize: options.cacheSize || 100,
      defaultTTL: options.cacheTTL || 5 * 60 * 1000 // 5 minutes by default
    };

    this.cache = new LRUCache(cacheOptions);
  }

  /**
   * Read a key; returns `null` if unset
   */
  async get<T>(key: string, opts?: { namespace?: string; bypassCache?: boolean }): Promise<{ value: T | null; version: number }> {
    const namespace = opts?.namespace || this.namespace;
    const cacheKey = `${namespace}:${key}`;

    // Try to get from cache first (if cache bypass not requested)
    if (!opts?.bypassCache) {
      const cachedItem = this.cache.get(cacheKey);
      if (cachedItem) {
        return cachedItem as { value: T | null; version: number };
      }
    }

    try {
      const response = await this.apiClient.get<ApiResponse<{ value: T | null; version: number }>>(`/api/storage/${namespace}`, {
        params: {
          gameId: this.gameId,
          key
        }
      });
      
      if (!response.data) {
        throw new Error('Failed to retrieve data from storage');
      }
      
      // Cache the response for future reads
      this.cache.set(cacheKey, response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error reading key from storage:', error);
      throw error;
    }
  }

  /**
   * Write a key with optional optimistic version check
   */
  async set<T>(key: string, value: T, opts?: { namespace?: string; version?: number }): Promise<{ version: number }> {
    const namespace = opts?.namespace || this.namespace;
    
    // Optional quota check before write
    if (this.quotaLimit !== null) {
      if (this.quotaUsage >= this.quotaLimit) {
        throw new Error('Storage quota exceeded');
      }
      
      // Approximate size calculation (this is very rough and depends on the data)
      const estimatedSize = JSON.stringify(value).length;
      if (this.quotaUsage + estimatedSize > this.quotaLimit) {
        throw new Error('Storage quota would be exceeded by this write');
      }
    }
    
    try {
      const response = await this.apiClient.post<ApiResponse<{ version: number }>>(`/api/storage/${namespace}`, {
        gameId: this.gameId,
        key,
        value,
        namespace,
        version: opts?.version
      });
      
      if (!response.data) {
        throw new Error('Failed to write data to storage');
      }
      
      // Make sure response is properly typed and has version property
      const responseData = response.data as unknown as { version: number };
      if (typeof responseData.version !== 'number') {
        throw new Error('Invalid version received from storage');
      }
      
      const version = responseData.version;
      
      // Update cache with new value
      const cacheKey = `${namespace}:${key}`;
      this.cache.set(cacheKey, { value, version });
      
      // Update quota usage (if tracking)
      if (this.quotaLimit !== null) {
        const estimatedSize = JSON.stringify(value).length;
        this.quotaUsage += estimatedSize;
      }
      
      return { version };
    } catch (error) {
      console.error('Error writing key to storage:', error);
      throw error;
    }
  }

  /**
   * Remove a key
   */
  async remove(key: string, opts?: { namespace?: string }): Promise<void> {
    const namespace = opts?.namespace || this.namespace;
    
    try {
      // Use DELETE request instead of setting null
      await this.apiClient.delete(`/api/storage/${namespace}`, {
        params: {
          gameId: this.gameId,
          key
        }
      });
      
      // Remove from cache
      const cacheKey = `${namespace}:${key}`;
      this.cache.delete(cacheKey);
      
    } catch (error) {
      console.error('Error removing key from storage:', error);
      throw error;
    }
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * List all keys in the namespace
   */
  async list<T>(opts?: { namespace?: string }): Promise<Array<{ namespace: string; key: string; value: T; version: number }>> {
    const namespace = opts?.namespace || this.namespace;
    
    try {
      const response = await this.apiClient.get<ApiResponse<Array<{ namespace: string; key: string; value: T; version: number }>>>(
        `/api/storage/${namespace}/list`
      );
      
      if (!response.data) {
        throw new Error('Failed to list storage items');
      }
      
      // Update cache with listed items
      response.data.forEach(item => {
        const cacheKey = `${namespace}:${item.key}`;
        this.cache.set(cacheKey, { value: item.value, version: item.version });
      });
      
      return response.data;
    } catch (error) {
      console.error('Error listing keys from storage:', error);
      throw error;
    }
  }
} 